/**
 * State shapes and pure helpers of the finance bulk import page: matching
 * server results to rows, counts, the apply input and the download files.
 */
import {
  FinanceBulkImportRowInput,
  FinanceBulkImportRowStatus,
} from '@/graphql/generatedTypes';
import { ValidateFinanceBulkImportMutation } from './financeBulkImport.generated';
import { CsvSeparator } from './csv';

export type ImportResult =
  ValidateFinanceBulkImportMutation['validateFinanceBulkImport'];
export type RowResult = ImportResult['rows'][number];

/** One data row of the file, as the user currently has it. */
export type ImportRow = {
  rowNumber: number;
  values: string[];
};

/** The file as the page holds it: normalised headers plus the rows. */
export type ImportFile = {
  fileName: string;
  separator: CsvSeparator;
  columns: string[];
  rows: ImportRow[];
};

export const ID_COLUMN = 'id';

export type EditorKind =
  | 'text'
  | 'days'
  | 'boolean'
  | 'blockType'
  | 'date'
  | 'country';

export const EDITOR_KIND: Record<string, EditorKind> = {
  id: 'text',
  vat_number: 'text',
  invoice_iban_name: 'text',
  invoice_headline: 'text',
  regular_payment_target_days: 'days',
  sepa_2_percent_discount: 'boolean',
  inkassokennzeichen: 'boolean',
  dunning_reminder_block_type: 'blockType',
  dunning_reminder_block_reason: 'text',
  dunning_reminder_block_until: 'date',
  invoice_address_country: 'country',
};

export const editorKindOf = (column: string): EditorKind =>
  EDITOR_KIND[column] ?? 'text';

export const cellValue = (
  file: ImportFile,
  row: ImportRow,
  column: string,
): string => row.values[file.columns.indexOf(column)] ?? '';

export function withCellValue(
  file: ImportFile,
  row: ImportRow,
  column: string,
  value: string,
): ImportRow {
  const index = file.columns.indexOf(column);
  const values = [...row.values];
  // An edited row is complete: the server refuses a row with fewer cells
  // than the header, and the table already shows the missing cells as empty.
  while (values.length < Math.max(index + 1, file.columns.length)) {
    values.push('');
  }
  values[index] = value;
  return { ...row, values };
}

export const resultsByRowNumber = (result: ImportResult | null) =>
  new Map((result?.rows ?? []).map((row) => [row.row_number, row]));

export type ImportCounts = Record<FinanceBulkImportRowStatus, number>;

export function countByStatus(rows: RowResult[]): ImportCounts {
  const counts: ImportCounts = {
    [FinanceBulkImportRowStatus.Error]: 0,
    [FinanceBulkImportRowStatus.Unchanged]: 0,
    [FinanceBulkImportRowStatus.Changed]: 0,
    [FinanceBulkImportRowStatus.Conflict]: 0,
    [FinanceBulkImportRowStatus.Applied]: 0,
  };
  rows.forEach((row) => {
    counts[row.status] += 1;
  });
  return counts;
}

export const toRowInputs = (file: ImportFile): FinanceBulkImportRowInput[] =>
  file.rows.map((row) => ({ row_number: row.rowNumber, values: row.values }));

/** Every row, each carrying the changes the latest validation showed for it. */
export function toApplyInputs(
  file: ImportFile,
  validation: ImportResult,
): FinanceBulkImportRowInput[] {
  const results = resultsByRowNumber(validation);
  return file.rows.map((row) => ({
    row_number: row.rowNumber,
    values: row.values,
    expected_changes: (results.get(row.rowNumber)?.changes ?? []).map(
      ({ column, old_value, new_value }) => ({ column, old_value, new_value }),
    ),
  }));
}

const messagesOf = (row: RowResult) =>
  row.errors
    .map((error) =>
      error.column ? `${error.column}: ${error.message}` : error.message,
    )
    .join(' | ');

/** The error rows in the file's own column layout, plus an errors column. */
export function errorRowsCsvData(
  file: ImportFile,
  validation: ImportResult,
): { header: string[]; rows: string[][] } {
  const results = resultsByRowNumber(validation);
  const rows = file.rows
    .filter(
      (row) =>
        results.get(row.rowNumber)?.status === FinanceBulkImportRowStatus.Error,
    )
    .map((row) => {
      const values = file.columns.map((_, index) => row.values[index] ?? '');
      return [...values, messagesOf(results.get(row.rowNumber)!)];
    });
  return { header: [...file.columns, 'errors'], rows };
}

export function resultCsvData(result: ImportResult): {
  header: string[];
  rows: string[][];
} {
  return {
    header: ['row_number', 'id', 'name', 'status', 'changes', 'messages'],
    rows: result.rows.map((row) => [
      String(row.row_number),
      row.id,
      row.name ?? '',
      row.status,
      row.changes
        .map((c) => `${c.column}: ${c.old_value} -> ${c.new_value}`)
        .join(' | '),
      messagesOf(row),
    ]),
  };
}
